import { type ExternalToast, toast } from "sonner";
import type { ToastTesterConfig } from "./toast-tester-config";

const PROMISE_SETTLE_MS = 2000;

type ButtonClickHandler = (label: string) => void;

/**
 * Maps a toast tester config to sonner options. `onButtonClick` is called
 * with the label of whichever action/cancel button the user presses.
 */
export function buildTestToastOptions(
  config: ToastTesterConfig,
  onButtonClick: ButtonClickHandler
): ExternalToast {
  return {
    duration: config.isPersistent
      ? Number.POSITIVE_INFINITY
      : config.durationSeconds * 1000,
    ...(config.description && { description: config.description }),
    ...(config.withAction && {
      action: {
        label: config.actionLabel,
        onClick: () => onButtonClick(config.actionLabel)
      }
    }),
    ...(config.withCancel && {
      cancel: {
        label: config.cancelLabel,
        onClick: () => onButtonClick(config.cancelLabel)
      }
    })
  };
}

/**
 * Title for the nth toast of a burst, numbered so stacked toasts can be told
 * apart.
 */
export function getTestToastTitle(
  config: ToastTesterConfig,
  position: number
): string {
  return config.count > 1 ? `${config.title} (${position})` : config.title;
}

function settleAfterDelay(shouldReject: boolean): Promise<void> {
  return new Promise((resolve, reject) => {
    setTimeout(() => {
      if (shouldReject) {
        reject(new Error("Rejected by toast tester"));
      } else {
        resolve();
      }
    }, PROMISE_SETTLE_MS);
  });
}

function showTestToast(
  config: ToastTesterConfig,
  title: string,
  options: ExternalToast
) {
  switch (config.type) {
    case "default":
      return toast(title, options);
    case "success":
      return toast.success(title, options);
    case "error":
      return toast.error(title, options);
    case "info":
      return toast.info(title, options);
    case "warning":
      return toast.warning(title, options);
    case "loading":
      return toast.loading(title, options);
    case "promise": {
      const { description, ...promiseOptions } = options;
      return toast.promise(settleAfterDelay(config.shouldPromiseReject), {
        ...promiseOptions,
        error: `${title} rejected`,
        loading: description ? `${title} — ${description}` : title,
        success: `${title} resolved`
      });
    }
    default: {
      const exhaustiveCheck: never = config.type;
      return exhaustiveCheck;
    }
  }
}

/**
 * Raises `config.count` toasts described by the tester config. Pressing an
 * action or cancel button raises a follow-up info toast so it is obvious the
 * handler fired.
 */
export function fireTestToast(config: ToastTesterConfig) {
  const options = buildTestToastOptions(config, label => {
    toast.info(`"${label}" clicked`);
  });

  const positions = Array.from(
    { length: config.count },
    (_, index) => index + 1
  );

  for (const position of positions) {
    showTestToast(config, getTestToastTitle(config, position), options);
  }
}
